import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { can } from "../lib/roles";
import { PageHeader } from "../components/Surface";
import { inputClass } from "../components/FormKit";
import { usePagedView, ViewToggle, Pagination } from "../components/Paged";
import { labelize } from "../lib/format";
import { adminListFeedback, adminUpdateFeedback, FEEDBACK_STATUSES, type FeedbackItem } from "../lib/feedback";

const TABS = FEEDBACK_STATUSES.map((s) => ({ key: s, label: labelize(s) }));

const typeTone: Record<string, string> = {
  bug: "text-danger", feature: "text-accent-strong", praise: "text-verified", general: "text-muted",
};

function Stars({ n }: { n: number | null }) {
  if (!n) return null;
  return <span className="font-mono text-sm text-accent" aria-label={`${n} of 5`}>{"★".repeat(n)}<span className="text-line">{"★".repeat(5 - n)}</span></span>;
}

export function AdminFeedback() {
  const { roles } = useAuth();
  const isStaff = can(roles, "admin_feedback");
  const [tab, setTab] = useState("new");
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState<string | null>(null);

  async function refresh(status = tab) {
    if (!isStaff) return;
    const r = await adminListFeedback({ status });
    setItems(r.feedback);
    setCounts(r.counts);
  }
  useEffect(() => {
    void refresh(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isStaff]);
  const { view, page, pageCount, pageItems, setView, setPage } = usePagedView(items, 12, "list");

  async function setStatus(id: string, status: string) {
    setBusy(id);
    try {
      await adminUpdateFeedback(id, status);
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  if (!isStaff) {
    return (
      <div className="grid gap-6">
        <PageHeader title="Feedback" subtitle="Support access required." />
        <p className="font-mono text-sm text-danger">Support staff only.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      <PageHeader title="Admin · Feedback" subtitle="Triage product feedback and feature requests." />

      <div className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold transition-colors ${tab === t.key ? "border-accent text-accent-strong" : "border-transparent text-muted hover:text-ink"}`}>
            {t.label} ({counts[t.key] ?? 0})
          </button>
        ))}
      </div>

      <div className="flex justify-end"><ViewToggle view={view} onChange={setView} /></div>

      {items.length === 0 ? (
        <p className="font-mono text-sm text-muted">Nothing here.</p>
      ) : (
        <ul className={view === "grid" ? "grid gap-3 lg:grid-cols-2" : "grid gap-3"}>
          {pageItems.map((f) => (
            <li key={f.id} className="grid gap-3 rounded-card border border-line bg-surface p-4 sm:grid-cols-[1fr_180px]">
              <div className="grid gap-1.5">
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-[11px] font-bold uppercase tracking-widest ${typeTone[f.type] ?? "text-muted"}`}>{f.type}</span>
                  <Stars n={f.rating} />
                </div>
                <p className="whitespace-pre-wrap text-sm">{f.message}</p>
                {f.attachments && f.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="feedback-attachments">
                    {f.attachments.map((a) => a.url && (
                      // Rendered ONLY as <img> from a signed URL — every file was magic-byte
                      // validated as a real raster image server-side, so nothing here can execute.
                      <a key={a.id} href={a.url} target="_blank" rel="noreferrer noopener">
                        <img src={a.url} alt="Feedback screenshot" loading="lazy" className="h-20 w-20 rounded-lg border border-line object-cover" />
                      </a>
                    ))}
                  </div>
                )}
                <p className="font-mono text-[10px] text-muted">{new Date(f.created_at).toLocaleString()}{typeof f.context?.route === "string" ? ` · ${f.context.route}` : ""}</p>
              </div>
              <div className="grid content-start gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-muted">Status</span>
                <select className={inputClass} value={f.status} disabled={busy === f.id} onChange={(e) => setStatus(f.id, e.target.value)} aria-label="Feedback status">
                  {FEEDBACK_STATUSES.map((s) => <option key={s} value={s}>{labelize(s)}</option>)}
                </select>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Pagination page={page} pageCount={pageCount} onPage={setPage} />
    </div>
  );
}
